/**
 * @module components/LegendColorSetting
 * @description 图例颜色设置（Phase 5 迁移 — DVA→Context）
 *
 * 状态管理模式：
 *   读: chartType → useQuerySelector；chartData → useResultSelector
 *   读/写: useSettingObjectState('legend_colors')
 *
 * 迁移前：DVA useSelector(chartType/chartData/legend_colors) + dispatch(onChangeDraft)
 * 迁移后：Context base/draft.legend_colors
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { SearchBox, Button } from 'tea-component';
import classNames from 'classnames';
import { AutoSizer, List } from 'react-virtualized';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import ColorPalettePopover, { type IColorPalettePopoverRef } from '@/pages/datalab/components/ColorPalettePopover';
import useVirtualReferenceElement from '@/pages/datalab/utils/hooks/useVirtualReferenceElement';
import { getLegendInfoFromChartData, buildLegendPaletteIndexMap, findRgbaForLegend, rgbaToColorInfo, colorInfoToRgba } from './utils';
import type { TLegendColors } from './types';
import type { IColorInfo } from '@/pages/datalab/components/ColorPalette';
import { LANGUAGE_EN } from '@home/_config/config';
import { useSettingObjectState } from '../../context';
import { useQuerySelector, useResultSelector } from '../../hooks/deps';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import './index.scss';

const LIST_MAX_HEIGHT = 280;
const LIST_ITEM_HEIGHT = 40;
const ENABLE_VIRTUAL_LIST_THRESHOLD = 20;

interface IEditingLegend { key: string; name: string; initialColor: IColorInfo; }

const LegendColorSetting: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { value: legendColors, setValue: setLegendColors } =
    useSettingObjectState('legend_colors');
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const chartDataFromContext = useResultSelector(s => s.chartData);
  const chartData = chartDataFromContext || ({} as Record<string, unknown> | undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const colorPalettePopoverRef = useRef<IColorPalettePopoverRef | null>(null);
  const [editingLegend, setEditingLegend] = useState<IEditingLegend | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');

  const { referenceElement, setAnchor } = useVirtualReferenceElement({ scrollContainerRef: containerRef });

  const { legendItems } = useMemo(() => getLegendInfoFromChartData(chartType, chartData, isEn), [chartType, chartData, isEn]);

  const allLegendColorList = useMemo(() => {
    const legendKeysInOrder = legendItems.map((item: { key: string }) => item.key);
    const paletteIndexMap = buildLegendPaletteIndexMap(legendKeysInOrder);
    return legendItems.map((item: { key: string; label: string }, originalIndex: number) => {
      const { key, label } = item;
      const paletteIndex = paletteIndexMap[key] ?? originalIndex;
      const rgba = findRgbaForLegend(key, paletteIndex, legendColors);
      return { key, name: label, rgba };
    });
  }, [legendItems, legendColors]);

  const displayList = useMemo(() => {
    const kw = searchKeyword.trim().toLowerCase();
    if (!kw) return allLegendColorList;
    return allLegendColorList.filter((item: { name: string }) => item.name.toLowerCase().includes(kw));
  }, [allLegendColorList, searchKeyword]);

  const enableVirtualList = displayList.length > ENABLE_VIRTUAL_LIST_THRESHOLD;

  useEffect(() => {
    if (!enableVirtualList) return;
    containerRef.current?.scrollTo({ top: 0 });
    setScrollTop(0);
  }, [enableVirtualList, searchKeyword]);

  const handleRestoreSettings = useCallback(() => {
    // 设置为空对象，使 findRgbaForLegend 回退到 LIGHT_RGBA_COLORS 默认颜色
    // 而非 base.legend_colors（上次保存的配置），确保重置为真正的默认颜色
    setLegendColors({});
    setEditingLegend(null);
    setAnchor(null);
  }, [setLegendColors, setAnchor]);

  const handleColorChange = useCallback((info: IColorInfo) => {
    const key = editingLegend?.key;
    if (!key) return;
    const next: TLegendColors = {
      ...legendColors,
      [key]: colorInfoToRgba(info),
    };
    setLegendColors(next);
  }, [editingLegend?.key, legendColors, setLegendColors]);

  const handleRowClick = useCallback((key: string, name: string, rgba: string, el: HTMLElement) => {
    const currentKey = editingLegend?.key;
    if (currentKey === key) { colorPalettePopoverRef.current?.submit?.(); setEditingLegend(null); setAnchor(null); return; }
    if (currentKey && currentKey !== key) { colorPalettePopoverRef.current?.submit?.(); }
    setAnchor(el);
    setEditingLegend({ key, name, initialColor: rgbaToColorInfo(rgba) });
  }, [editingLegend?.key]);

  return (
    <ConfigCollapseCard title={formatMessage({ id: 'dbl_chart_color_setting_title' })} className="dbl-legend-color-setting" defaultActiveIds={[]}>
      <div className="dbl-legend-color-setting__box">
        <SearchBox className="dbl-legend-color-setting__search" placeholder={formatMessage({ id: 'dbl_chart_legend_color_search' })} value={searchKeyword} onChange={setSearchKeyword} onSearch={(k: string | undefined) => setSearchKeyword(k ?? '')} onClear={() => setSearchKeyword('')} />
        <div className="dbl-legend-color-setting__header">
          <span className="dbl-legend-color-setting__header-label">{formatMessage({ id: 'dbl_chart_legend_setting' })}</span>
          <Button type="link" className="dbl-legend-color-setting__header-restore" onClick={(e: React.MouseEvent) => { e?.preventDefault?.(); e?.stopPropagation?.(); handleRestoreSettings(); }}>{formatMessage({ id: 'dbl_chart_color_setting_restore' })}</Button>
        </div>
        <div className="dbl-legend-color-setting__list-wrap" style={{ maxHeight: LIST_MAX_HEIGHT }} ref={containerRef} onScroll={(e: React.UIEvent<HTMLDivElement>) => { if (!enableVirtualList) return; setScrollTop(e.currentTarget.scrollTop); }}>
          {displayList.length === 0 ? <div className="dbl-legend-color-setting__empty">{formatMessage({ id: 'dbl_common_no_data2' })}</div> : (
            <div className="dbl-legend-color-setting__list">
              {enableVirtualList ? (
                <AutoSizer disableHeight>{({ width }: { width: number }) => <List autoHeight height={LIST_MAX_HEIGHT} width={width} rowCount={displayList.length} rowHeight={LIST_ITEM_HEIGHT} scrollTop={scrollTop} rowRenderer={({ index, key, style }: { index: number; key: string; style: React.CSSProperties }) => { const item = displayList[index]; return <div key={key} style={style}><LegendColorRow legendKey={item.key} name={item.name} rgba={item.rgba} isSelected={editingLegend?.key === item.key} onClick={handleRowClick} /></div>; }} />}</AutoSizer>
              ) : displayList.map((item: { key: string; name: string; rgba: string }) => <LegendColorRow key={item.key} legendKey={item.key} name={item.name} rgba={item.rgba} isSelected={editingLegend?.key === item.key} onClick={handleRowClick} />)}
            </div>
          )}
        </div>
      </div>
      <ColorPalettePopover ref={colorPalettePopoverRef} open={Boolean(editingLegend)} value={editingLegend ? { hexa: editingLegend.initialColor.hexa || '#ffffff', alpha: editingLegend.initialColor.alpha || '100' } : undefined} onChange={handleColorChange} onVisibleChange={(visible: boolean) => { if (!visible) { setEditingLegend(null); setAnchor(null); } }} popoverProps={{ referenceElement, trigger: 'click', placement: 'left', overlayClassName: 'dbl-legend-color-setting__popover-overlay' }} />
    </ConfigCollapseCard>
  );
};

interface ILegendColorRowProps { legendKey: string; name: string; rgba: string; isSelected: boolean; onClick: (legendKey: string, name: string, rgba: string, el: HTMLElement) => void; }
const LegendColorRowComponent: React.FC<ILegendColorRowProps> = ({ legendKey, name, rgba, isSelected, onClick }) => (
  <div className={classNames('dbl-legend-color-setting__row', { 'dbl-legend-color-setting__row--selected': isSelected })} onClick={(e: React.MouseEvent) => onClick(legendKey, name, rgba, e.currentTarget as HTMLElement)} role="button" tabIndex={0} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(legendKey, name, rgba, e.currentTarget as HTMLElement); } }}>
    <div className="dbl-legend-color-setting__color-block"><div className="dbl-legend-color-setting__color-inner" style={{ backgroundColor: rgba }} /></div>
    <span className="dbl-legend-color-setting__name" title={name}>{name}</span>
  </div>
);
const LegendColorRow = React.memo(LegendColorRowComponent);

export default React.memo(LegendColorSetting);
